import { GenericAbortSignal } from 'axios';

import http from 'services/http';

export const adminDemoSetupRunsQueryKeys = {
  index: (params?: Record<string, any>) => ['adminDemoSetupRuns', params] as const,
  show: (id: string) => ['adminDemoSetupRuns', id] as const,
  eligibleGroups: (role: 'owner' | 'post_acute', ownerGroupId?: string, trainingType?: string) =>
    ['adminDemoSetupRuns', 'eligibleGroups', role, ownerGroupId, trainingType] as const,
};

const URL = 'admin/demo_setup_runs';

export type DemoSetupGroupOption = {
  id: string;
  name: string;
  groupType: string | null;
  clientName: string | null;
};

export type DemoSetupRunGroupStatus = 'queued' | 'in_progress' | 'success' | 'failed';

export type DemoSetupRunGroup = {
  id: string;
  status: DemoSetupRunGroupStatus;
  itemCounts: Record<string, number>;
  errorMessage: string | null;
  warnings: string[];
  startedAt: string | null;
  completedAt: string | null;
  group: { id: string; name: string };
};

export type TrainingType = 'snf' | 'home_health';

export type DemoSetupRun = {
  id: string;
  scenario: string;
  summary: Record<string, number>;
  trainingType?: TrainingType;
  archiveExisting?: boolean;
  createdAt: string;
  updatedAt: string;
  triggeredBy: { id: string; name: string };
  ownerGroup: { id: string; name: string; groupType?: string | null };
  runGroups: DemoSetupRunGroup[];
};

type Paginated<T> = {
  data: T[];
  meta: { totalPages: number; totalRecords: number };
  links: { next: string | null; prev: string | null };
};

export type AdminCreateDemoSetupRunParams = {
  ownerGroupId: string;
  postAcuteGroupIds: string[];
  scenario?: string;
  trainingType?: TrainingType;
  archiveExisting?: boolean;
};

export async function adminCreateDemoSetupRun(
  params: AdminCreateDemoSetupRunParams,
  signal?: GenericAbortSignal
): Promise<DemoSetupRun> {
  const body = {
    owner_group_id: params.ownerGroupId,
    post_acute_group_ids: params.postAcuteGroupIds,
    scenario: params.scenario ?? 'default',
    training_type: params.trainingType ?? 'snf',
    archive_existing: params.archiveExisting ?? false,
  };
  return http.post<DemoSetupRun>(URL, body, { signal }).then((res) => res.data);
}

export async function adminShowDemoSetupRun(id: string, signal?: GenericAbortSignal): Promise<DemoSetupRun> {
  return http.get<DemoSetupRun>(`${URL}/${id}`, { signal }).then((res) => res.data);
}

export async function adminIndexDemoSetupRuns(
  params: { page?: number; perPage?: number; days?: number } = {},
  signal?: GenericAbortSignal
): Promise<Paginated<DemoSetupRun>> {
  return http.get<Paginated<DemoSetupRun>>(URL, { params, signal }).then((res) => res.data);
}

export async function adminEligibleDemoSetupGroups(
  args: { role: 'owner' | 'post_acute'; ownerGroupId?: string; trainingType?: TrainingType },
  signal?: GenericAbortSignal
): Promise<DemoSetupGroupOption[]> {
  return http
    .get<{ data: DemoSetupGroupOption[] }>(`${URL}/eligible_groups`, {
      params: {
        role: args.role,
        owner_group_id: args.ownerGroupId,
        training_type: args.trainingType,
      },
      signal,
    })
    .then((res) => res.data.data);
}
